import {
  Budget,
  TabularError,
  makeSheet,
  type TabularResult,
  type Cell,
} from "./types";
export function decode(
  bytes: Uint8Array,
  encoding: "auto" | "utf-8" | "utf-16le" | "utf-16be" | "windows-1252",
) {
  let selected = encoding;
  if (selected === "auto") {
    if (bytes[0] === 255 && bytes[1] === 254) selected = "utf-16le";
    else if (bytes[0] === 254 && bytes[1] === 255) selected = "utf-16be";
    else if (bytes[0] === 239 && bytes[1] === 187 && bytes[2] === 191)
      selected = "utf-8";
    else {
      try {
        new TextDecoder("utf-8", { fatal: true }).decode(bytes);
        selected = "utf-8";
      } catch {
        selected = "windows-1252";
      }
    }
  }
  try {
    const text = new TextDecoder(selected, { fatal: true }).decode(bytes);
    if (text.includes("\0"))
      throw new TabularError(
        "NULL_CHARACTER",
        "Null characters are not allowed",
      );
    return { text, encoding: selected as Exclude<typeof selected, "auto"> };
  } catch (error) {
    if (error instanceof TabularError) throw error;
    throw new TabularError(
      "ENCODING",
      "Input cannot be decoded with the selected encoding",
    );
  }
}
function delimiterOf(text: string) {
  const counts = new Map([
    [",", 0],
    ["\t", 0],
    [";", 0],
    ["|", 0],
  ]);
  let quoted = false,
    rows = 0;
  for (let i = 0; i < Math.min(text.length, 65536) && rows < 10; i++) {
    const c = text[i];
    if (c === '"') {
      if (quoted && text[i + 1] === '"') i++;
      else quoted = !quoted;
    } else if (!quoted) {
      if (counts.has(c)) counts.set(c, counts.get(c)! + 1);
      if (c === "\n" || c === "\r") rows++;
    }
  }
  return [...counts].sort((a, b) => b[1] - a[1])[0][0];
}
export function parseText(bytes: Uint8Array, budget: Budget): TabularResult {
  const decoded = decode(bytes, budget.options.encoding),
    text = decoded.text,
    delimiter = budget.options.delimiter || delimiterOf(text);
  const records: { row: number; cells: Cell[] }[] = [];
  let nonempty = 0,
    row = 1,
    cells: Cell[] = [],
    value = "",
    quoted = false,
    afterQuote = false,
    started = false;
  const cell = () => {
    cells.push(budget.cell(value, 0, row, cells.length + 1));
    value = "";
    afterQuote = false;
    started = false;
  };
  const record = () => {
    cell();
    records.push({ row, cells });
    if (cells.some((c) => c.value !== "")) nonempty++;
    if (
      nonempty >
      budget.limits.rows +
        1 +
        (typeof budget.options.headerRow === "number"
          ? budget.options.headerRow
          : 0)
    )
      throw new TabularError("ROW_LIMIT", "Sheet exceeds its row limit");
    row++;
    cells = [];
  };
  for (let i = 0; i < text.length; i++) {
    if (i % 256 === 0) budget.check();
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          value += '"';
          i++;
        } else {
          quoted = false;
          afterQuote = true;
        }
      } else value += c;
    } else if (c === delimiter) cell();
    else if (c === "\r" || c === "\n") {
      record();
      if (c === "\r" && text[i + 1] === "\n") i++;
    } else if (c === '"') {
      if (started || afterQuote || value)
        throw new TabularError(
          "CSV_SYNTAX",
          "Unexpected quote in an unquoted field",
        );
      quoted = true;
      started = true;
    } else {
      if (afterQuote)
        throw new TabularError(
          "CSV_SYNTAX",
          "Unexpected text after a quoted field",
        );
      value += c;
      started = true;
    }
    if (value.length > budget.limits.cellChars)
      throw new TabularError("CELL_LIMIT", "Cell text exceeds its limit");
    if (cells.length >= budget.limits.columns && c === delimiter)
      throw new TabularError("COLUMN_LIMIT", "Sheet exceeds its column limit");
  }
  if (quoted) throw new TabularError("CSV_SYNTAX", "Unclosed quoted field");
  if (value || cells.length || started || afterQuote) record();
  budget.check();
  return {
    version: 1,
    format: delimiter === "," ? "csv" : "delimited",
    encoding: decoded.encoding,
    delimiter,
    sheets: [makeSheet("Data", 0, records, budget)],
    diagnostics: budget.diagnostics,
  };
}
